import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { DOCS_URL } from '@/lib/constants'

import { Button } from '@/components/ui/button'
import {
  LayoutDashboard,
  Globe,
  Mail,
  KeyRound,
  Webhook,
  FileText,
  Inbox,
  TrendingUp,
  CreditCard,
  Settings,
  ChevronLeft,
  ChevronRight,
  Globe2,
  Calendar,
  Shuffle,
  Users,
  BookOpen,
  Download,
} from 'lucide-react'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  mobileOpen: boolean
  onMobileClose: () => void
}

// CHECKPOINT: PRD-10 §4.3 — Sidebar docs links and navigation sections.
export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation()
  const navSections = [
    {
      label: 'Main',
      items: [{ icon: LayoutDashboard, label: 'Overview', path: '/' }],
    },
    {
      label: 'Email',
      items: [
        { icon: Globe, label: 'Domains', path: '/domains' },
        { icon: Mail, label: 'Mailboxes', path: '/mailboxes' },
        { icon: Shuffle, label: 'Aliases', path: '/aliases' },
        { icon: Users, label: 'Contacts', path: '/contacts' },
        { icon: Download, label: 'Migration', path: '/migration' },
        { icon: KeyRound, label: 'API Keys', path: '/api-keys' },
        { icon: Webhook, label: 'Webhooks', path: '/webhooks' },
        { icon: FileText, label: 'Templates', path: '/templates' },
      ],
    },
    {
      label: 'Activity',
      items: [
        { icon: Calendar, label: 'Scheduled', path: '/scheduled' },
        { icon: Inbox, label: 'Inbox', path: '/inbox' },
        { icon: TrendingUp, label: 'Deliverability', path: '/deliverability' },
      ],
    },
    {
      label: 'Apps',
      items: [
        // CHECKPOINT: PRD-10 FR-10.14.6 — Single-click "Open Webmail" button in dashboard sidebar.
        { icon: Globe2, label: 'Webmail', path: '/webmail' },
      ],
    },
    {
      label: 'Account',
      items: [
        { icon: CreditCard, label: 'Usage & Billing', path: '/billing' },
        { icon: Settings, label: 'Settings', path: '/settings' },
        { icon: BookOpen, label: 'Docs', path: DOCS_URL, external: true },
      ],
    },
  ]
  const renderNav = () => (
    <>
      <div className="flex h-[68px] items-center justify-between border-b border-border/80 px-4">
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2.5 animate-fade-in">
            <img src="/logo.svg" alt="PurpleToad Mail" className="h-8 w-8" />
            <span className="text-sm font-semibold tracking-[-0.025em]">PurpleToad Mail</span>
          </Link>
        )}
        {collapsed && (
          <img src="/logo.svg" alt="PurpleToad Mail" className="h-8 w-8 mx-auto" />
        )}
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-7 w-7', collapsed && 'hidden')}
          onClick={onToggle}
          aria-label="Collapse sidebar"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {collapsed && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onToggle} aria-label="Expand sidebar">
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-5">
        {navSections.map((section) => (
          <div key={section.label} className="mb-5">
            {!collapsed && (
              <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground/65">
                {section.label}
              </p>
            )}
            {section.items.map((item: any) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
              return item.external ? (
                <a key={item.path} href={item.path} target="_blank" rel="noopener noreferrer"
                  className={cn('mx-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-[background-color,color] duration-150', 'hover:bg-accent hover:text-accent-foreground', collapsed && 'justify-center px-2')}
                  title={collapsed ? item.label : undefined}>
                  <item.icon className="h-4 w-4 shrink-0 transition-colors" />
                  {!collapsed && <span>{item.label}</span>}
                </a>
              ) : (
                <Link key={item.path} to={item.path} onClick={onMobileClose}
                  className={cn('mx-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-[background-color,color] duration-150', 'hover:bg-accent hover:text-accent-foreground', isActive && 'bg-primary/12 text-foreground font-medium', collapsed && 'justify-center px-2')}
                  title={collapsed ? item.label : undefined}>
                  <item.icon className={cn('h-4 w-4 shrink-0 transition-colors', isActive && 'text-primary')} />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              )
            })}
          </div>
        ))}


      </div>
    </>
  )

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 hidden h-screen flex-col border-r border-border/80 bg-card/90 backdrop-blur-xl transition-[width] duration-200 md:flex',
          collapsed ? 'w-[68px]' : 'w-[240px]'
        )}
      >
        {renderNav()}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-screen w-[280px] flex-col border-r border-border/80 bg-card md:hidden transition-transform duration-200',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {renderNav()}
      </aside>
    </>
  )
}
